'use client';

import Image from "next/image";
import Link from "next/link";
import NavBarSecond from "./NavBar/NavBarSecond";
import { AutoBreadcrumbs } from "./Breadcrumbs/AutoBreadcrumbs";
import ContactForm from "./ContactForm";

interface BlogPost {
  title: string;
  date: string;
  image: string;
  slug: string;
}

interface BlogLayoutProps {
  title: string;
  date: string;
  featuredImage: string;
  featuredImageAlt?: string;
  content: React.ReactNode;
  recentBlogs: BlogPost[];
  description?: string;
  keywords?: string[];
  author?: string;
  slug?: string;
}

export default function BlogLayout({
  title,
  date,
  featuredImage,
  featuredImageAlt,
  content,
  recentBlogs,
  author = "Speech Clinic Dubai",
}: BlogLayoutProps) {
  const publishedLabel = (() => {
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return date;
    return parsed.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  })();

  return (
    <>
      <NavBarSecond />
      <main className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-4 pt-6 lg:pt-16 pb-2">
          <AutoBreadcrumbs currentLabel={title} className="mb-2" />
        </div>
        <section
          className="relative h-[60vh] w-full"
          aria-label="Blog header"
        >
          <Image
            src={featuredImage}
            alt={featuredImageAlt || `Featured image for ${title}`}
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="text-center text-white max-w-4xl px-4">
              <h1 className="text-4xl md:text-5xl font-medium mb-4">{title}</h1>
              <p className="text-lg md:text-xl">
                Published on {publishedLabel}
                {author ? ` · ${author}` : null}
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <article className="lg:col-span-2 prose prose-lg max-w-none">
              {content}
            </article>

            <aside className="space-y-8" aria-label="Related content">
              <section aria-labelledby="recent-blogs-heading">
                <h2 id="recent-blogs-heading" className="text-2xl font-medium mb-6 text-gray-900">
                  Recent Blogs
                </h2>
                <div className="space-y-6">
                  {recentBlogs.map((blog, index) => (
                    <Link
                      href={`/blogs/${blog.slug}`}
                      key={index}
                      className="block group"
                      aria-label={`Read more about ${blog.title}`}
                    >
                      <div className="flex gap-4 items-start">
                        <div className="relative w-24 h-24 flex-shrink-0">
                          <Image
                            src={blog.image}
                            alt={`Thumbnail for ${blog.title}`}
                            fill
                            className="object-cover rounded-lg"
                          />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                            {blog.title}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {new Date(blog.date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>

              <section aria-labelledby="contact-form-heading">
                <h2 id="contact-form-heading" className="text-2xl font-medium mb-6 text-gray-900">
                  Contact Us
                </h2>
                <ContactForm />
              </section>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
}
